import React, { useState, useEffect } from "react";
import withStyles from "utils/withStyles";
import styles from "./styles";
import "./styles.css";
import SearchBox from "../common/SearchBox";
import ContentHeader from "../common/ContentHeader";
import { ApplyButton } from "components/Common/Buttons";
import { TableGenerator } from "../../../../../Common";
import moment from "dayjs";
import Grid from "utils/Grid";
import { Typography, Card, Button, CircularProgress } from "@mui/material";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import CustomInputDatePicker from "components/Common/CustomInputDatePicker";
import { CloudDownload } from "@mui/icons-material";
import { handleMultiFilterSearch, downloadBlob } from "helpers";
import {
  getFilterStartDate,
  getFilterEndDate,
  getTimeZoneDifference,
} from "utils/helpers";
import _ from "lodash";
import { useSelector } from "react-redux";
import {
  convertUtcToLocal,
  dateFormatter,
  currencyFormatter,
} from "utils/helpers";

const filterLabels = {
  payment_id: "transaction_id",
  order_id: "order_id",
  transaction_id: "payment_id",
};

const deviceTypeFields = ["payment_id", "order_id", "transaction_id"];

const MonthlyStatement = () => {
  const classes = styles();
  const { deviceId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [deviceTypeList, setDeviceList] = useState([]);
  const [modulesSelected, setModulesSelected] = useState([]);
  const [filterable, setFilterable] = useState(deviceTypeFields);
  const [query, setQuery] = useState("");
  const [loader, setLoader] = useState(false);
  const [ordering, setOrdering] = useState("-timestamp");
  const [startDate, setStartDate] = useState(
    moment().subtract(15, "days").toDate()
  );
  const [endDate, setEndDate] = useState(new Date());
  const [nextPage, setNextPage] = useState(null);
  const [previousPage, setPreviousPage] = useState(null);
  const [limit, setLimit] = useState(25);
  const [page, setPage] = useState(0);
  const [searchQuery, setSearchQuery] = useState({});
  const [exportLoader, setExportLoader] = useState(false);
  const current_user = useSelector((state) => state.userReducer.current_user);
  const fields = [
    {
      key: "transaction_id",
      columnName: "Vending Payment Id",
      type: "text",
      visible: true,
    },
    {
      key: "order_id",
      columnName: "Order Id",
      type: "text",
      visible: true,
    },
    {
      key: "payment_id",
      columnName: "Transaction Id",
      type: "text",
      visible: true,
      render: (value) => value || "---",
    },
    {
      key: "status",
      columnName: "Transaction Status",
      type: "text",
      visible: true,
    },
    {
      key: "gateway",
      columnName: "Gateway",
      type: "text",
      visible: true,
    },
    {
      key: "amount",
      columnName: "Amount ($)",
      type: "number",
      visible: true,
      render: (value) => currencyFormatter(value) ?? "---",
    },
    {
      key: "order_date",
      columnName: "Transaction time",
      type: "text",
      visible: true,
      render: (value) => dateFormatter(convertUtcToLocal(value), true) ?? "---",
    },
  ];

  const setup = () => {
    setLoader(true);
    setDeviceList([]);
    setModulesSelected([]);
  };

  const handleRes = (data = []) => {
    setLoader(false);
    setDeviceList(data?.results);
    setNextPage(data.next);
    setPreviousPage(data.previous);
  };

  useEffect(() => {
    fetchTransaction(ordering, page, limit);
  }, []);

  const changePage = (url) => {
    setup();
    window.axiosIns
      .get(url)
      .then(({ data = {} }) => {
        handleRes(data);
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const handleFilter = (arr) => {
    setFilterable(arr);
    if (query !== "") {
      let searchFilter = handleMultiFilterSearch(filterLabels, arr, query);
      setup();
      setPage(0);
      window
        .axiosIns("transactions/custom-transactions", {
          params: {
            ...searchFilter,
            limit: limit,
            filter_device_id: deviceId,
            start_date: getFilterStartDate(startDate),
            end_date: getFilterEndDate(endDate),
          },
        })
        .then((data = {}) => {
          handleRes(data.data);
        })
        .catch((err) => {
          setLoader(false);
        });
    }
  };

  const handleSearch = (value) => {
    setQuery(value);
    let searchFilter = {};
    if (value !== "") {
      searchFilter = handleMultiFilterSearch(filterLabels, filterable, value);
    }
    setSearchQuery(searchFilter);
    setup();
    setPage(0);
    window
      .axiosIns("transactions/custom-transactions", {
        params: {
          ...searchFilter,
          limit: limit,
          filter_device_id: deviceId,
          start_date: getFilterStartDate(startDate),
          end_date: getFilterEndDate(endDate),
        },
      })
      .then((data = {}) => {
        handleRes(data.data);
      })
      .catch((err) => {
        setLoader(false);
      });
  };

  const fetchTransaction = async (order, pageNumber, limit) => {
    setLimit(limit);
    const realPage = pageNumber ? 1 : page + 1;
    const params = {
      ...searchQuery,
      ordering: order,
      page: realPage,
      limit: limit,
      start_date: getFilterStartDate(startDate),
      end_date: getFilterEndDate(endDate),
      history: true,
      latest: true,
      _scope: "AND",
      filter_device_id: deviceId,
    };
    setLoader(true);

    try {
      const { data } = await window.axiosIns(
        "transactions/custom-transactions",
        {
          params,
        }
      );
      handleRes(data);
      setLoader(false);
    } catch (err) {
      setLoader(false);
    }
  };

  const fetchExcel = async () => {
    setExportLoader(true);
    const params = {
      ...searchQuery,
      start_date: getFilterStartDate(startDate),
      end_date: getFilterEndDate(endDate),
      tz: getTimeZoneDifference(),
      _scope: "AND",
      swap_payment_id: true,
      filter_device_id: deviceId,
    };
    if (
      "order_id" in params ||
      "payment_id" in params ||
      "transaction_id" in params
    ) {
      params["_scope"] = "OR";
    }
    try {
      const { data } = await window.axiosIns(
        "transactions/export_transaction_summary",
        {
          responseType: "blob",
          params,
        }
      );

      if (data) {
        downloadBlob(
          new Blob([data], { type: "application/xlsx" }),
          "transactions_report.xlsx"
        );
      }

      setExportLoader(false);
    } catch (err) {
      setExportLoader(false);
    }
  };

  return (
    <div id="sa-modules-wrapper" className={classes.wrapper}>
      <ContentHeader
        description="
        All transactions automatically show up here. You can filter that statements."
      />
      <Card
        style={{ margin: "0px 20px 10px 20px" }}
        variant="contained"
        className={classes.headerToolbar}
      >
        <Grid style={{ padding: "10px 20px" }} container>
          <Grid xs={12} sm={4}>
            <Typography variant="subtitle2">
              <strong>VM Serial : </strong>
              {location?.state?.serial_number || ""}
            </Typography>
          </Grid>
          <Grid xs={12} sm={4}>
            <Typography variant="subtitle2">
              <strong>VM Name : </strong>
              {location?.state?.vm_name || ""}
            </Typography>
          </Grid>
        </Grid>
      </Card>
      <div className={classes.toolbar}>
        <Grid
          spacing={1}
          style={{ alignItems: "center" }}
          container
          className="align-center"
        >
          <Grid md={2} xs={3}>
            <CustomInputDatePicker
              inputClassName=""
              value={startDate}
              onChange={(date) => {
                setStartDate(date);
                setPage(0);
              }}
              label="Start Date"
              maxDate={new Date(endDate).setDate(new Date(endDate).getDate())}
              disabled={loader}
            />
          </Grid>

          <Grid md={2} xs={3}>
            <CustomInputDatePicker
              inputClassName=""
              value={endDate}
              onChange={(date) => {
                setEndDate(date);
                setPage(0);
              }}
              label="End Date"
              minDate={new Date(startDate)}
              maxDate={new Date()}
              disabled={loader}
            />
          </Grid>
          <Grid md={1} xs={1}>
            <ApplyButton
              style={{ height: 56, width: "100%" }}
              onClick={() => fetchTransaction(ordering, page, limit)}
            />
          </Grid>
          <Grid md={4} xs={4}>
            <SearchBox
              style={{ height: 56 }}
              iconStyle={{ height: 56 }}
              multiple={true}
              query={query}
              onChange={handleFilter}
              fields={deviceTypeFields}
              selectedFields={filterable}
              handleSearch={handleSearch}
            />
          </Grid>
          <Grid md="auto" xs="auto">
            {current_user.type !== "SU" && deviceTypeList.length >= 0 && (
              <Button
                style={{ height: 56, padding: "0 20px" }}
                size="small"
                color="primary"
                variant="contained"
                disabled={exportLoader || !deviceTypeList.length}
                onClick={fetchExcel}
              >
                {exportLoader ? (
                  <>
                    <CircularProgress
                      color="#fff"
                      size={16}
                      style={{ marginRight: 0 }}
                    />
                  </>
                ) : (
                  <>
                    <CloudDownload className="mr-1" fontSize="small" />{" "}
                  </>
                )}
                Export
              </Button>
            )}
          </Grid>
          <Grid md={1} xs={1}>
            <Button
              style={{ height: 56, width: "100%" }}
              size="small"
              className="text-red mr-1"
              color="primary"
              variant="contained"
              onClick={() =>
                navigate("/finance?tab=orders_and_transactions")
              }
            >
              Close
            </Button>
          </Grid>
        </Grid>
      </div>
      <div className={classes.content}>
        <TableGenerator
          searchQuery={query}
          minimal={true}
          nextPage={nextPage}
          previousPage={previousPage}
          initialSort={"id"}
          searchColumnsFilter={true}
          fields={fields}
          data={deviceTypeList}
          loader={loader}
          currentPage={page}
          handleSortChange={(ordering) => {
            setOrdering(ordering);
            fetchTransaction(ordering, page, limit);
          }}
          onPageChange={(page, direction) => {
            setPage(page);
            if (direction === "next") {
              changePage(nextPage);
            } else if (direction === "back") {
              changePage(previousPage);
            }
          }}
          backendPagination={true}
          onRowPerPageChange={(rows) => {
            setLimit(rows);
            setPage(0);
            fetchTransaction(ordering, page, rows);
          }}
          selectedRecords={modulesSelected}
          rowOnePage={25}
          onChangePage={(page) => console.log(page)}
          onChangeSelected={(selectedInterface) =>
            setModulesSelected(selectedInterface)
          }
        />
      </div>
    </div>
  );
};
export default withStyles({}, { withTheme: true })(MonthlyStatement);
